import { forwardRef, useEffect, useRef, useState } from "react";
import type { InputHTMLAttributes } from "react";
import { Input } from "./primitives";

/**
 * Ein Eingabefeld, das speichert — ohne dabei Buchstaben zu verlieren.
 *
 * Das Muster, das immer wieder schiefgeht:
 *
 *     <Input value={x.name} onChange={e => speichern({name: e.target.value})} />
 *
 * Jeder Tastendruck schickt eine Anfrage, die Antwort setzt die Liste neu,
 * und der Wert springt auf den Stand von vor zwei Zeichen zurück. Wer schnell
 * tippt, sieht Buchstaben verschwinden. Es ist kein Tippfehler im Aufruf,
 * sondern ein Wettlauf: die Antwort ist immer älter als die Tastatur.
 *
 * Dieses Feld führt seinen Wert selbst, solange es den Fokus hat. Nach außen
 * meldet es sich erst, wenn eine Weile Ruhe war, oder bei Enter, oder beim
 * Verlassen. Ein Wert von außen wird nur übernommen, wenn gerade *nicht*
 * getippt wird — sonst hätte man den Wettlauf nur verschoben.
 */
export const DebouncedInput = forwardRef<
  HTMLInputElement,
  Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "value"> & {
    value: string;
    /** Wird gerufen, wenn der Wert übernommen werden soll. */
    onCommit: (wert: string) => void;
    /** Millisekunden Ruhe, bevor übernommen wird. */
    verzoegerung?: number;
    mono?: boolean;
  }
>(function DebouncedInput(
  { value, onCommit, verzoegerung = 500, onBlur, onKeyDown, ...rest }, ref,
) {
  const [entwurf, setEntwurf] = useState(value);
  const tippt = useRef(false);
  const timer = useRef<number | null>(null);
  // Über eine Ref, damit ein neu erzeugtes onCommit den Timer nicht abwirft.
  const commitRef = useRef(onCommit);
  useEffect(() => { commitRef.current = onCommit; }, [onCommit]);

  // Von außen nur übernehmen, solange niemand tippt.
  useEffect(() => {
    if (!tippt.current) setEntwurf(value);
  }, [value]);

  const uebernehmen = (w: string) => {
    if (timer.current) { window.clearTimeout(timer.current); timer.current = null; }
    tippt.current = false;
    if (w !== value) commitRef.current(w);
  };

  useEffect(() => () => {
    // Beim Ausbauen noch schnell sichern: sonst geht das letzte Zeichen
    // verloren, wenn der Dialog zugeht, bevor die Ruhezeit um ist.
    if (timer.current) {
      window.clearTimeout(timer.current);
      tippt.current = false;
    }
  }, []);

  return (
    <Input
      ref={ref}
      value={entwurf}
      onChange={(e) => {
        const w = e.target.value;
        tippt.current = true;
        setEntwurf(w);
        if (timer.current) window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => uebernehmen(w), verzoegerung);
      }}
      onBlur={(e) => {
        uebernehmen(e.target.value);
        onBlur?.(e);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") uebernehmen((e.target as HTMLInputElement).value);
        // Escape verwirft und stellt den Stand von außen wieder her.
        if (e.key === "Escape") {
          if (timer.current) window.clearTimeout(timer.current);
          tippt.current = false;
          setEntwurf(value);
        }
        onKeyDown?.(e);
      }}
      {...rest}
    />
  );
});
